// OrderReceiptPDF.jsx
import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import AppanCourierLogo from "../../assets/Appan Courier Logo.png";

// ----------------- Styles -----------------
const styles = StyleSheet.create({
  page: {
    backgroundColor: "#FFFFFF",
    fontFamily: "Helvetica",
    fontSize: 10,
  },
  sectionA: {},
  sectionB: {
    padding: 25,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingBottom: 10,
    backgroundColor: "rgba(242,107,0,0.10)",
    padding: 25,
  },
  logoContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 25,
  },
  logo: {
    width: 90,
    height: 50,
    borderRadius: 6,
  },
  companyInfo: {
    flexDirection: "column",
  },
  companyName: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#1F2937",
  },
  companyDetails: {
    fontSize: 9,
    color: "#6B7280",
    lineHeight: 1.2,
  },
  title: {
    color: "#F26B00",
    fontWeight: "bold",
    marginTop: 15,
    marginBottom: 8,
    fontSize: 11,
  },
  infoGrid: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 10,
  },
  infoCard: {
    border: "1 solid #E5E7EB",
    borderRadius: 6,
    padding: 6,
    flex: 1,
    backgroundColor: "#fff",
  },
  infoRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: 3,
    flexWrap: "wrap", // ✅ allows text to break into multiple lines
  },
  label: {
    color: "#6B7280",
    fontSize: 9,
    width: "40%", // ✅ left column width for keys
  },
  value: {
    color: "#111827",
    fontSize: 9,
    fontWeight: "bold",
    width: "60%", // ✅ right column width for values
    flexShrink: 1,
    flexWrap: "wrap", // ✅ wraps long address lines
  },
  valueNormal: {
    color: "#6B7280",
    fontSize: 9,
    fontWeight: "normal", // ✅ non-bold for address values
    width: "60%", // ✅ right column width for normal values
    flexShrink: 1,
    flexWrap: "wrap",
  },
  tableContainer: {
    marginTop: 12,
    borderTop: "1 dashed #E5E7EB",
    paddingTop: 8,
  },
  tableHeader: {
    flexDirection: "row",
    borderBottom: "1 solid #E5E7EB",
    paddingVertical: 4,
  },
  tableColDescription: { width: "40%", paddingLeft: 4 },
  tableColQty: { width: "15%", textAlign: "center" },
  tableColWeight: { width: "20%", textAlign: "right" },
  tableColPrice: { width: "25%", textAlign: "right", paddingRight: 4 },
  tableCell: {
    paddingVertical: 3,
    borderBottom: "1 dashed #E5E7EB",
  },
  tableHeaderText: {
    color: "#6B7280",
    fontWeight: "bold",
  },
  paymentSection: {
    marginTop: 14,
  },
  paymentBox: {
    border: "1 solid #E5E7EB",
    borderRadius: 6,
    padding: 8,
    flex: 1,
    backgroundColor: "#fff",
  },
  paymentRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  paymentLabel: {
    fontWeight: "bold",
    color: "#6B7280",
  },
  signatures: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 25,
    paddingHorizontal: 15,
  },
  signBlock: {
    alignItems: "center",
    width: "45%",
  },
  signLine: {
    borderTop: "1 solid #000",
    marginTop: 35,
    width: "100%",
  },
  signText: {
    marginTop: 5,
    fontSize: 9,
    color: "#6B7280",
  },
});

// ----------------- Component -----------------
const OrderReceiptPDF = ({ receiptData, formatDate }) => {
  const calculateLineTotal = (item) => {
    const price = parseFloat(item.pricePerUnit) || 0;
    const qty = parseFloat(item.quantity) || 1;
    return (price * qty).toLocaleString("en-IN", { minimumFractionDigits: 2 });
  };

  const calculateGrandTotal = () => {
    const total =
      receiptData.productRows?.reduce((sum, item) => {
        const price = parseFloat(item.pricePerUnit) || 0;
        const qty = parseFloat(item.quantity) || 1;
        return sum + price * qty;
      }, 0) || 0;
    return total.toLocaleString("en-IN", { minimumFractionDigits: 2 });
  };

  const grandTotal = calculateGrandTotal();
  const formattedReceiptDate = receiptData.receiptDate
    ? formatDate(receiptData.receiptDate)
    : "";

  const isLmbpBusinessType = receiptData?.lmbpBusinessType === "SELLER";

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.sectionA}>
          {/* Header */}
          <View style={styles.header}>
            <View style={styles.logoContainer}>
              <Image style={styles.logo} src={AppanCourierLogo} />
              <View style={styles.companyInfo}>
                <Text style={styles.companyName}>
                  {receiptData.companyName}
                </Text>
                <Text style={styles.companyDetails}>
                  {receiptData.companyAddress}
                </Text>
                <Text style={styles.companyDetails}>
                  {receiptData.companyContact}
                </Text>
              </View>
            </View>
          </View>
        </View>

        <View style={styles.sectionB}>
          {/* Title */}
          <Text style={styles.title}>Customer Order Receipt</Text>

          {/* Info Cards */}
          <View style={styles.infoGrid}>
            {/* Left Info Card */}
            <View style={styles.infoCard}>
              <View style={styles.infoRow}>
                <Text style={styles.label}>Receipt No:</Text>
                <Text style={styles.value}>C-{receiptData.receiptNumber}</Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.label}>Date:</Text>
                <Text style={styles.value}>{formattedReceiptDate}</Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.label}>Express Type:</Text>
                <Text style={styles.value}>
                  {receiptData.expressType || "N/A"}
                </Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.label}>Consignor Name:</Text>
                <Text style={styles.value}>{receiptData.customerName}</Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.label}>Consignor Contact:</Text>
                <Text style={styles.value}>{receiptData.customerContact}</Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.label}>Consignor Address:</Text>
                <Text style={styles.valueNormal}>
                  {receiptData.customerAddress}
                </Text>
              </View>
            </View>

            {/* Right Info Card */}
            <View style={styles.infoCard}>
              <View style={styles.infoRow}>
                <Text style={styles.label}>Order ID:</Text>
                <Text style={styles.value}>{receiptData.orderId}</Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.label}>Waybill No:</Text>
                <Text style={styles.value}>{receiptData.ourWaybillNumber}</Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.label}>Consignee Name:</Text>
                <Text style={styles.value}>{receiptData.consigneeName}</Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.label}>Consignee Contact:</Text>
                <Text style={styles.value}>{receiptData.consigneeContact}</Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.label}>Pickup Address:</Text>
                <Text style={styles.valueNormal}>
                  {receiptData.pickupLocation}
                </Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.label}>Delivery Address:</Text>
                <Text style={styles.valueNormal}>
                  {receiptData.deliveryAddress}
                </Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.label}>Return Address:</Text>
                <Text style={styles.valueNormal}>
                  {receiptData.returnAddress}
                </Text>
              </View>
            </View>
          </View>

          {/* Order Details */}
          <View style={styles.tableContainer}>
            <Text style={styles.title}>Order Details</Text>
            <View style={styles.tableHeader}>
              <View style={styles.tableColDescription}>
                <Text style={styles.tableHeaderText}>Item Description</Text>
              </View>
              <View style={styles.tableColQty}>
                <Text style={styles.tableHeaderText}>Qty</Text>
              </View>
              <View style={styles.tableColWeight}>
                <Text style={styles.tableHeaderText}>Weight</Text>
              </View>
              <View style={styles.tableColPrice}>
                <Text style={styles.tableHeaderText}>Product Cost</Text>
              </View>
            </View>

            {receiptData.productRows?.map((item, idx) => (
              <View key={idx} style={{ flexDirection: "row" }}>
                <View style={[styles.tableColDescription, styles.tableCell]}>
                  <Text>{item.description}</Text>
                </View>
                <View style={[styles.tableColQty, styles.tableCell]}>
                  <Text>{item.quantity}</Text>
                </View>
                <View style={[styles.tableColWeight, styles.tableCell]}>
                  <Text>{item.weight} kg</Text>
                </View>
                <View style={[styles.tableColPrice, styles.tableCell]}>
                  <Text>{`Rs. ${calculateLineTotal(item)}`}</Text>
                </View>
              </View>
            ))}
          </View>

          {/* Payment Details */}
          <View style={styles.paymentSection}>
            <Text style={styles.title}>Payment Details</Text>
            <View style={{ flexDirection: "row", gap: 10, marginTop: 5 }}>
              {/* Left: Payment Details (Freight, Fuel, GST) */}
              <View style={styles.paymentBox}>
                <View style={styles.paymentRow}>
                  <Text style={styles.paymentLabel}>Freight Charge:</Text>
                  {isLmbpBusinessType ? (
                    <Text>Rs. {receiptData?.amount.toFixed(2)}</Text>
                  ) : (
                    <Text></Text>
                  )}
                </View>
                <View style={styles.paymentRow}>
                  <Text style={styles.paymentLabel}>Fuel Charge:</Text>
                  {isLmbpBusinessType ? (
                    <Text>Rs. {receiptData?.fuelCharges.toFixed(2)}</Text>
                  ) : (
                    <Text></Text>
                  )}
                </View>
                <View style={styles.paymentRow}>
                  <Text style={styles.paymentLabel}>
                    GST[{receiptData?.gstPercent}%]:
                  </Text>
                  {isLmbpBusinessType ? (
                    <Text>Rs. {receiptData?.gstAmount.toFixed(2)}</Text>
                  ) : (
                    <Text></Text>
                  )}
                </View>
              </View>

              {/* Right: Weight & Total Amount */}
              <View style={styles.paymentBox}>
                <View style={styles.paymentRow}>
                  <Text style={styles.paymentLabel}>Weight:</Text>
                  {isLmbpBusinessType ? (
                    <Text>{receiptData?.weight.toFixed(2)} kg</Text>
                  ) : (
                    <Text></Text>
                  )}
                </View>
                <View style={styles.paymentRow}>
                  <Text style={styles.paymentLabel}>COD Amount:</Text>
                  {isLmbpBusinessType ? (
                    <Text>Rs. {receiptData?.codAmount.toFixed(2)}</Text>
                  ) : (
                    <Text></Text>
                  )}
                </View>
                <View style={styles.paymentRow}>
                  <Text style={styles.paymentLabel}>Total Amount:</Text>
                  {isLmbpBusinessType ? (
                    <Text>Rs. {receiptData?.totalAmount.toFixed(2)}</Text>
                  ) : (
                    <Text></Text>
                  )}
                </View>
              </View>
            </View>
          </View>

          {/* Signatures */}
          <View style={styles.signatures}>
            <View style={styles.signBlock}>
              <View style={styles.signLine}></View>
              <Text style={styles.signText}>Authorized Signatory</Text>
            </View>
            <View style={styles.signBlock}>
              <View style={styles.signLine}></View>
              <Text style={styles.signText}>Customer Signature</Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default OrderReceiptPDF;
